import { apiClient } from "./api-client";

export interface CaseRecordDto {
  id: string;
  title: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  participant: {
    id: string;
    firstName: string;
    lastName: string;
    nationalId?: string | null;
  };
  assignedTo?: {
    id: string;
    email: string;
    role: string;
  } | null;
  _count?: {
    applications: number;
    tasks: number;
    notes: number;
  };
}

export interface BenefitApplicationDto {
  id: string;
  applicantName: string;
  applicantEmail: string;
  program: string;
  status: string;
  submittedAt: string;
  case?: {
    id: string;
    title: string;
    status: string;
  } | null;
}

export interface CaseTaskDto {
  id: string;
  title: string;
  status: string;
  dueDate: string;
  case?: {
    id: string;
    title: string;
    status: string;
  } | null;
  assignedTo?: {
    id: string;
    email: string;
  } | null;
}

export interface ProgramDto {
  id: string;
  name: string;
  description?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ParticipantEnrollmentDto {
  id: string;
  programId: string;
  status: string;
  startedAt: string;
  endedAt?: string | null;
  program: {
    id: string;
    name: string;
    status: string;
  };
}

export interface ParticipantDto {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  nationalId?: string | null;
  createdAt: string;
  updatedAt: string;
  enrollments: ParticipantEnrollmentDto[];
}

export interface ProgramInput {
  name: string;
  description?: string;
  status?: string;
}

export interface ParticipantInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  notes?: string;
  nationalId?: string;
}

export interface ParticipantAssignmentInput {
  programId: string;
  status?: string;
  startedAt?: string;
  endedAt?: string | null;
}

export interface UserDto {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserInput {
  email: string;
  password: string;
  role: string;
}

interface StatusBreakdownDto {
  status: string;
  count: number;
}

export interface CaseDashboardResponse {
  cases: CaseRecordDto[];
  stats: {
    total: number;
    byStatus: Record<string, number>;
  };
}

export interface CaseDetailDto {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  participant: ParticipantDto;
  assignedTo?: {
    id: string;
    email: string;
    role: string;
  } | null;
  applications: Array<BenefitApplicationDto & { program?: ProgramDto | null }>;
  tasks: CaseTaskDto[];
  notes: Array<{
    id: string;
    content: string;
    noteType: string;
    isPrivate: boolean;
    createdAt: string;
    author: { id: string; email: string };
  }>;
  documents: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    category: string;
    createdAt: string;
    uploader: { id: string; email: string };
  }>;
  activities: Array<{
    id: string;
    action: string;
    description: string;
    metadata?: Record<string, unknown> | null;
    createdAt: string;
    user?: { id: string; email: string } | null;
  }>;
}

export interface DashboardDatasetDto {
  caseStatusBreakdown: StatusBreakdownDto[];
  applicationStatusBreakdown: StatusBreakdownDto[];
  programEnrollmentTop: Array<{ programId: string; programName: string; count: number }>;
}

export interface CaseOptionsDto {
  participants: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    nationalId?: string | null;
  }>;
  staff: Array<{
    id: string;
    email: string;
    role: string;
  }>;
}

export const adminDataApi = {
  async getCases() {
    const { data } = await apiClient.get<{
      status: string;
      data: { cases: CaseRecordDto[]; stats: { total: number; byStatus: Record<string, number> } };
    }>("/data/cases");
    return data.data;
  },
  async getCase(caseId: string) {
    const { data } = await apiClient.get<{ status: string; data: CaseDetailDto }>(
      `/data/cases/${caseId}`
    );
    return data.data;
  },
  async getCaseOptions() {
    const { data } = await apiClient.get<{ status: string; data: CaseOptionsDto }>(
      "/data/cases/options"
    );
    return data.data;
  },
  async createCase(payload: {
    title: string;
    description?: string;
    participantId: string;
    assignedToId?: string;
    priority?: string;
    initialNote?: string;
  }) {
    const { data } = await apiClient.post<{ status: string; data: CaseDetailDto }>(
      "/data/cases",
      payload
    );
    return data.data;
  },
  async updateCase(caseId: string, payload: Partial<{
    title: string;
    description: string;
    status: string;
    priority: string;
    assignedToId: string | null;
  }>) {
    const { data } = await apiClient.patch<{ status: string; data: CaseDetailDto }>(
      `/data/cases/${caseId}`,
      payload
    );
    return data.data;
  },
  async addCaseNote(caseId: string, payload: { content: string; noteType?: string; isPrivate?: boolean }) {
    const { data } = await apiClient.post<{ status: string; data: unknown }>(
      `/data/cases/${caseId}/notes`,
      payload
    );
    return data.data;
  },
  async getApplications() {
    const { data } = await apiClient.get<{ status: string; data: BenefitApplicationDto[] }>(
      "/data/applications"
    );
    return data.data;
  },
  async createApplication(payload: {
    applicantName: string;
    applicantEmail: string;
    program: string;
    caseId?: string;
  }) {
    const { data } = await apiClient.post<{ status: string; data: BenefitApplicationDto }>(
      "/data/applications",
      payload
    );
    return data.data;
  },
  async updateApplication(
    id: string,
    payload: {
      applicantName?: string;
      applicantEmail?: string;
      program?: string;
      status?: string;
      caseId?: string | null;
    }
  ) {
    const { data } = await apiClient.patch<{ status: string; data: BenefitApplicationDto }>(
      `/data/applications/${id}`,
      payload
    );
    return data.data;
  },
  async getTasks() {
    const { data } = await apiClient.get<{ status: string; data: CaseTaskDto[] }>(
      "/data/tasks"
    );
    return data.data;
  },
  async createTask(payload: {
    title: string;
    dueDate: string;
    caseId?: string;
    assignedToId?: string;
  }) {
    const { data } = await apiClient.post<{ status: string; data: CaseTaskDto }>(
      "/data/tasks",
      payload
    );
    return data.data;
  },
  async updateTask(
    id: string,
    payload: {
      title?: string;
      status?: string;
      dueDate?: string;
      caseId?: string | null;
      assignedToId?: string | null;
    }
  ) {
    const { data } = await apiClient.patch<{ status: string; data: CaseTaskDto }>(
      `/data/tasks/${id}`,
      payload
    );
    return data.data;
  },
  async deleteTask(id: string) {
    await apiClient.delete(`/data/tasks/${id}`);
  },
  async getPrograms() {
    const { data } = await apiClient.get<{ status: string; data: ProgramDto[] }>(
      "/programs"
    );
    return data.data;
  },
  async createProgram(payload: ProgramInput) {
    const { data } = await apiClient.post<{ status: string; data: ProgramDto }>(
      "/programs",
      payload
    );
    return data.data;
  },
  async updateProgram(id: string, payload: ProgramInput) {
    const { data } = await apiClient.patch<{ status: string; data: ProgramDto }>(
      `/programs/${id}`,
      payload
    );
    return data.data;
  },
  async getParticipants() {
    const { data } = await apiClient.get<{ status: string; data: ParticipantDto[] }>(
      "/participants"
    );
    return data.data;
  },
  async getParticipant(id: string) {
    const { data } = await apiClient.get<{ status: string; data: ParticipantDto }>(
      `/participants/${id}`
    );
    return data.data;
  },
  async createParticipant(payload: ParticipantInput) {
    const { data } = await apiClient.post<{ status: string; data: ParticipantDto }>(
      "/participants",
      payload
    );
    return data.data;
  },
  async updateParticipant(id: string, payload: ParticipantInput) {
    const { data } = await apiClient.patch<{ status: string; data: ParticipantDto }>(
      `/participants/${id}`,
      payload
    );
    return data.data;
  },
  async assignParticipantProgram(id: string, payload: ParticipantAssignmentInput) {
    const { data } = await apiClient.post<{ status: string; data: ParticipantEnrollmentDto }>(
      `/participants/${id}/programs`,
      payload
    );
    return data.data;
  },
  async updateParticipantProgram(
    participantId: string,
    programId: string,
    payload: {
      status?: string;
      endedAt?: string | null;
    }
  ) {
    const { data } = await apiClient.patch<{ status: string; data: ParticipantEnrollmentDto }>(
      `/participants/${participantId}/programs/${programId}`,
      payload
    );
    return data.data;
  },
  async removeParticipantProgram(participantId: string, programId: string) {
    await apiClient.delete(`/participants/${participantId}/programs/${programId}`);
  },
  async getUsers() {
    const { data } = await apiClient.get<{ status: string; data: UserDto[] }>(
      "/users"
    );
    return data.data;
  },
  async getUserById(id: string) {
    const { data } = await apiClient.get<{ status: string; data: UserDto }>(
      `/users/${id}`
    );
    return data.data;
  },
  async createUser(payload: UserInput) {
    const { data } = await apiClient.post<{ status: string; data: UserDto }>(
      "/users",
      payload
    );
    return data.data;
  },
  async updateUser(id: string, payload: Partial<UserInput>) {
    const { data } = await apiClient.patch<{ status: string; data: UserDto }>(
      `/users/${id}`,
      payload
    );
    return data.data;
  },
  async deleteUser(id: string) {
    await apiClient.delete(`/users/${id}`);
  }
};
